import { createFileRoute } from "@tanstack/react-router";
import { BookingProvider } from "@/components/site/booking-context";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { MobileBookingBar } from "@/components/site/MobileBookingBar";
import {
  RatesHero,
  RatesIntro,
  DailyRates,
  WeeklyRates,
  MonthlyRates,
  RateComparison,
  MidBookingCTA,
  RateIncludes,
  OccupancyPolicy,
  ExtendedStayProcess,
  ExtendedStayWaitlist,
  ElectricityDeposit,
  PaymentAndDumping,
  RatesDestinationBridge,
  RatesFAQ,
  RatesCTA,
} from "@/components/site/rates";
import { heroPreloadLink } from "@/data/optimized-images";
import { rateFaqs, resort } from "@/data/resort";
import { absoluteUrl } from "@/lib/site";
import heroMainAsset from "@/assets/auburn-hero-main.webp.asset.json";


const title = "RV Site Rates in Auburn, CA | Daily, Weekly & Monthly | Auburn RV Resort";
const description =
  "View daily, weekly and monthly RV site rates at Auburn RV Resort in Auburn, California. Daily stays start at $85, weekly at $504 and monthly at $1,260.";
const ogImage = `https://auburnrvresort.com${heroMainAsset.url}`;


const schema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["LodgingBusiness", "Campground"],
      name: resort.name,
      description,
      telephone: resort.phone,
      email: resort.email,
      url: "/rates/",
      address: {
        "@type": "PostalAddress",
        streetAddress: resort.address.street,
        addressLocality: resort.address.city,
        addressRegion: resort.address.state,
        postalCode: resort.address.zip,
        addressCountry: "US",
      },
      makesOffer: [
        {
          "@type": "Offer",
          name: "Daily RV Site",
          price: "85",
          priceCurrency: "USD",
          description: "Daily full-hookup RV site rates starting at $85 per night.",
        },
        {
          "@type": "Offer",
          name: "Weekly RV Site",
          price: "484",
          priceCurrency: "USD",
          description: "Weekly full-hookup RV site rates starting at $504 per week.",
        },
        {
          "@type": "Offer",
          name: "Monthly RV Site",
          price: "1210",
          priceCurrency: "USD",
          description: "Monthly month-to-month RV site rates starting at $1,260.",
        },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: rateFaqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

export const Route = createFileRoute("/rates")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:image", content: ogImage },
      { property: "og:image:width", content: "1322" },
      { property: "og:image:height", content: "936" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/rates/") }, heroPreloadLink],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(schema) }],
  }),
  component: RatesPage,
});

function RatesPage() {
  return (
    <BookingProvider>
      <Navbar />
      <main>
        <RatesHero />
        <RatesIntro />
        <DailyRates />
        <WeeklyRates />
        <MonthlyRates />
        <RateComparison />
        <MidBookingCTA />
        <RateIncludes />
        <OccupancyPolicy />
        <ExtendedStayProcess />
        <ExtendedStayWaitlist />
        <ElectricityDeposit />
        <PaymentAndDumping />
        <RatesDestinationBridge />
        <RatesFAQ />
        <RatesCTA />
      </main>
      <Footer />
      <MobileBookingBar />
    </BookingProvider>
  );
}
